import {
  AlertTriangle,
  Bell,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Clock3,
  Edit3,
  FileDown,
  Gavel,
  History,
  ImagePlus,
  KeyRound,
  ListChecks,
  LogOut,
  Maximize2,
  Package,
  Phone,
  Pin,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Share2,
  ShieldCheck,
  Star,
  ShoppingBag,
  Trash2,
  UploadCloud,
  UserRound,
  Users,
  X,
} from "lucide-react";
import {
  FormEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  bidProduct,
  createAdmin,
  createProduct,
  deleteProduct,
  getAdminEvents,
  getAdminStatus,
  getAdmins,
  getEvents,
  getProduct,
  getProducts,
  getProductBids,
  loginAdmin,
  logoutAdmin,
  resetProduct,
  reserveProduct,
  setProductMode,
  updateAdminPassword,
  updateProduct,
} from "./lib/api";
import { compactUserAgent, formatBaht, formatDateTime } from "./lib/format";
import { convertToWebp } from "./lib/images";
import type { AdminAccount, BidHistory, ConvertedImage, LiveEvent, Product, ProductMode } from "./types";

type Filter = "all" | "available" | "reserved" | "auction";
type ToastTone = "success" | "danger" | "neutral";

interface ToastMessage {
  id: number;
  title: string;
  message: string;
  tone: ToastTone;
}

interface ActionTarget {
  product: Product;
  action: "reserve" | "bid";
}

interface LightboxTarget {
  product: Product;
  imageIndex: number;
}

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: "all", label: "ทั้งหมด" },
  { id: "available", label: "พร้อมจอง" },
  { id: "auction", label: "ประมูล" },
  { id: "reserved", label: "จองแล้ว" },
];

function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}วัน ${hours}ชม.`;
  if (hours > 0) return `${hours}ชม. ${minutes}น.`;
  if (minutes > 0) return `${minutes}น. ${seconds}วิ`;
  return `${seconds}วิ`;
}

function getAuctionState(product: Product, now: number) {
  if (product.mode !== "auction") return { label: "", isActive: false, isEnded: false };
  const startAt = product.auctionStartAt ? Date.parse(product.auctionStartAt) : null;
  const endAt = product.auctionEndAt ? Date.parse(product.auctionEndAt) : null;
  if (startAt && Number.isFinite(startAt) && now < startAt) {
    return { label: `เริ่มใน ${formatDuration(startAt - now)}`, isActive: false, isEnded: false };
  }
  if (endAt && Number.isFinite(endAt) && now > endAt) {
    return { label: "ปิดประมูลแล้ว", isActive: false, isEnded: true };
  }
  if (endAt && Number.isFinite(endAt)) {
    return { label: `เหลือ ${formatDuration(endAt - now)}`, isActive: true, isEnded: false };
  }
  return { label: "เปิดประมูล", isActive: true, isEnded: false };
}

function toDatetimeLocal(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function localDatetimeToIso(value: FormDataEntryValue | null): string {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function getProductShortCode(product: Product): string {
  return product.id.replace(/-/g, "").slice(0, 12);
}

export default function App() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminUsername, setAdminUsername] = useState<string | null>(null);
  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);
  const [detailProduct, setDetailProduct] = useState<Product | null>(null);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [lightbox, setLightbox] = useState<LightboxTarget | null>(null);
  const [requestedProductId, setRequestedProductId] = useState(() => new URLSearchParams(window.location.search).get("product"));
  const [busyProductId, setBusyProductId] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const lastEventId = useRef(0);
  const toastCounter = useRef(0);

  const stats = useMemo(() => {
    const available = products.filter((product) => product.status === "available" && product.mode === "sale").length;
    const auction = products.filter((product) => product.mode === "auction").length;
    const reserved = products.filter((product) => product.status === "reserved").length;
    return { available, auction, reserved, total: products.length };
  }, [products]);

  const filteredProducts = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return products.filter((product) => {
      const matchesSearch =
        !keyword ||
        [product.title, product.description, product.dimensions, product.note]
          .join(" ")
          .toLowerCase()
          .includes(keyword);
      const matchesFilter =
        filter === "all" ||
        (filter === "available" && product.status === "available" && product.mode === "sale") ||
        (filter === "reserved" && product.status === "reserved") ||
        (filter === "auction" && product.mode === "auction");
      return matchesSearch && matchesFilter;
    });
  }, [filter, products, search]);

  useEffect(() => {
    void refreshProducts();
    void getAdminStatus()
      .then((status) => {
        setIsAdmin(status.isAdmin);
        setAdminUsername(status.username ?? null);
      })
      .catch(() => {
        setIsAdmin(false);
        setAdminUsername(null);
      });
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!requestedProductId || loading) return;
    const listedProduct = products.find((product) => product.id === requestedProductId);
    if (listedProduct) {
      setDetailProduct(listedProduct);
      clearRequestedProductParam();
      return;
    }

    let active = true;
    void getProduct(requestedProductId)
      .then(({ product }) => {
        if (!active) return;
        setDetailProduct(product);
        setProducts((current) => (current.some((item) => item.id === product.id) ? current : [product, ...current]));
        clearRequestedProductParam();
      })
      .catch(() => {
        if (!active) return;
        pushToast("ไม่พบสินค้า", "ลิงก์สินค้านี้อาจถูกลบแล้ว", "danger");
        clearRequestedProductParam();
      });

    return () => {
      active = false;
    };
  }, [loading, products, requestedProductId]);

  useEffect(() => {
    let active = true;
    let interval: number | undefined;

    async function startPolling() {
      try {
        const initial = await getEvents();
        lastEventId.current = Math.max(0, ...initial.events.map((event) => event.id));
      } catch {
        lastEventId.current = 0;
      }

      interval = window.setInterval(async () => {
        if (!active) return;
        try {
          const { events } = await getEvents(lastEventId.current);
          if (events.length > 0) {
            lastEventId.current = Math.max(lastEventId.current, ...events.map((event) => event.id));
            events.forEach((event) => pushEventToast(event));
            void refreshProducts(false);
          }
        } catch {
          // Notification polling should not interrupt the workspace.
        }
      }, 4000);
    }

    void startPolling();
    return () => {
      active = false;
      if (interval) window.clearInterval(interval);
    };
  }, []);

  async function refreshProducts(showSpinner = true) {
    if (showSpinner) setLoading(true);
    try {
      const data = await getProducts();
      setProducts(data.products);
    } catch (error) {
      pushToast("โหลดรายการไม่สำเร็จ", error instanceof Error ? error.message : "กรุณาลองใหม่", "danger");
    } finally {
      if (showSpinner) setLoading(false);
    }
  }

  function pushToast(title: string, message: string, tone: ToastTone = "neutral") {
    const id = ++toastCounter.current;
    setToasts((current) => [...current, { id, title, message, tone }].slice(-4));
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 4500);
  }

  function pushEventToast(event: LiveEvent) {
    const titleByType: Record<LiveEvent["type"], string> = {
      product_added: "เพิ่มสินค้าใหม่",
      reserved: "มีการจองสินค้า",
      bid: "มีการประมูลใหม่",
      deleted: "ลบสินค้าแล้ว",
      mode_changed: "เปลี่ยนรูปแบบสินค้า",
      product_updated: "แก้ไขสินค้า",
      reset: "รีเซ็ตสินค้า",
      admin_created: "เพิ่ม Admin",
      admin_password_changed: "เปลี่ยนรหัสผ่าน Admin",
    };
    pushToast(titleByType[event.type] ?? "อัปเดตล่าสุด", event.message, event.type === "deleted" ? "danger" : "success");
  }

  function clearRequestedProductParam() {
    setRequestedProductId(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("product");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }

  async function handleDelete(product: Product) {
    const confirmed = window.confirm(`ลบสินค้า "${product.title}" หรือไม่`);
    if (!confirmed) return;
    setBusyProductId(product.id);
    try {
      await deleteProduct(product.id);
      setProducts((current) => current.filter((item) => item.id !== product.id));
      setDetailProduct((current) => (current?.id === product.id ? null : current));
      pushToast("ลบสินค้าแล้ว", product.title, "danger");
    } catch (error) {
      pushToast("ลบสินค้าไม่สำเร็จ", error instanceof Error ? error.message : "กรุณาลองใหม่", "danger");
    } finally {
      setBusyProductId(null);
    }
  }

  async function handleModeChange(product: Product, mode: ProductMode) {
    setBusyProductId(product.id);
    try {
      const { product: updated } = await setProductMode(product.id, mode);
      setProducts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setDetailProduct((current) => (current?.id === updated.id ? updated : current));
      pushToast("เปลี่ยนโหมดแล้ว", `${updated.title} เป็น${mode === "auction" ? "ประมูล" : "จองสินค้า"}`, "success");
    } catch (error) {
      pushToast("เปลี่ยนโหมดไม่สำเร็จ", error instanceof Error ? error.message : "กรุณาลองใหม่", "danger");
    } finally {
      setBusyProductId(null);
    }
  }

  async function handleReset(product: Product) {
    const message =
      product.mode === "auction"
        ? `รีเซ็ต "${product.title}" เพื่อยกเลิกการประมูลและลบประวัติการเสนอราคาทั้งหมดหรือไม่`
        : `รีเซ็ต "${product.title}" เพื่อยกเลิกการจองหรือไม่`;
    const confirmed = window.confirm(message);
    if (!confirmed) return;

    setBusyProductId(product.id);
    try {
      const { product: updated } = await resetProduct(product.id);
      setProducts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      setDetailProduct((current) => (current?.id === updated.id ? updated : current));
      pushToast("รีเซ็ตสินค้าแล้ว", updated.title, "success");
    } catch (error) {
      pushToast("รีเซ็ตสินค้าไม่สำเร็จ", error instanceof Error ? error.message : "กรุณาลองใหม่", "danger");
    } finally {
      setBusyProductId(null);
    }
  }

  async function handleExport(product: Product) {
    setBusyProductId(product.id);
    try {
      const { exportProductPdf } = await import("./lib/pdf");
      await exportProductPdf(product);
      pushToast("ส่งออก PDF แล้ว", product.title, "success");
    } catch (error) {
      pushToast("ส่งออก PDF ไม่สำเร็จ", error instanceof Error ? error.message : "กรุณาลองใหม่", "danger");
    } finally {
      setBusyProductId(null);
    }
  }

  async function handleShare(product: Product) {
    const url = `${window.location.origin}/p/${getProductShortCode(product)}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: product.title,
          text: `${product.title} - ${formatBaht(product.priceCents)}`,
          url,
        });
        return;
      }
      await navigator.clipboard.writeText(url);
      pushToast("คัดลอกลิงก์แล้ว", url, "success");
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(url);
        pushToast("คัดลอกลิงก์แล้ว", url, "success");
      } catch {
        window.prompt("Short URL สำหรับแชร์สินค้า", url);
      }
    }
  }

  async function openProductFromEvent(productId: string | null) {
    if (!productId) return;
    let product = products.find((item) => item.id === productId) ?? null;
    if (!product) {
      try {
        const data = await getProducts();
        setProducts(data.products);
        product = data.products.find((item) => item.id === productId) ?? null;
      } catch {
        product = null;
      }
    }
    if (!product) {
      pushToast("ไม่พบสินค้า", "รายการนี้อาจถูกลบแล้ว", "danger");
      return;
    }
    setDetailProduct(product);
    setAdminOpen(false);
  }

  return (
    <div className="app-shell">
      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((current) => current.filter((toast) => toast.id !== id))} />
      <header className="app-header">
        <div className="brand-row">
          <div>
            <p className="eyebrow">Internal office marketplace</p>
            <h1>Aplay Office</h1>
          </div>
          <button className="icon-label-button subtle" type="button" onClick={() => setAdminOpen(true)} data-testid="open-admin">
            <ShieldCheck size={18} aria-hidden="true" />
            {isAdmin ? "Admin" : "Login"}
          </button>
        </div>

        <div className="header-actions">
          <div className="search-field">
            <Search size={18} aria-hidden="true" />
            <label className="sr-only" htmlFor="product-search">
              ค้นหาสินค้า
            </label>
            <input
              id="product-search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="ค้นหาชื่อสินค้า รายละเอียด หรือขนาด"
              type="search"
            />
          </div>
          <button className="primary-button" type="button" onClick={() => setShowAddProduct(true)} data-testid="open-add-product">
            <Plus size={20} aria-hidden="true" />
            เพิ่มสินค้า
          </button>
        </div>

        <div className="stats-strip" aria-label="สรุปรายการสินค้า">
          <Metric label="ทั้งหมด" value={stats.total} />
          <Metric label="พร้อมจอง" value={stats.available} />
          <Metric label="ประมูล" value={stats.auction} />
          <Metric label="จองแล้ว" value={stats.reserved} />
        </div>
      </header>

      <main className="workspace" id="main-content">
        <div className="toolbar">
          <div className="segmented-control" aria-label="ตัวกรองสินค้า">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                className={filter === item.id ? "active" : ""}
                type="button"
                onClick={() => setFilter(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <button className="icon-button" type="button" onClick={() => void refreshProducts()} aria-label="รีเฟรชรายการ">
            <RefreshCw size={18} aria-hidden="true" />
          </button>
        </div>

        {loading ? (
          <ProductSkeleton />
        ) : filteredProducts.length === 0 ? (
          <EmptyState onAdd={() => setShowAddProduct(true)} />
        ) : (
          <section className="product-grid" aria-label="รายการสินค้า">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                isAdmin={isAdmin}
                busy={busyProductId === product.id}
                now={now}
                onAction={(action) => setActionTarget({ product, action })}
                onDelete={() => void handleDelete(product)}
                onEdit={() => setEditProduct(product)}
                onExport={() => void handleExport(product)}
                onOpenImage={(imageIndex) => setLightbox({ product, imageIndex })}
                onModeChange={(mode) => void handleModeChange(product, mode)}
                onOpenDetails={() => setDetailProduct(product)}
                onReset={() => void handleReset(product)}
                onShare={() => void handleShare(product)}
              />
            ))}
          </section>
        )}
      </main>

      <button className="floating-add" type="button" onClick={() => setShowAddProduct(true)} aria-label="เพิ่มสินค้า" data-testid="floating-add-product">
        <Plus size={24} aria-hidden="true" />
      </button>

      {showAddProduct && (
        <AddProductModal
          onClose={() => setShowAddProduct(false)}
          onCreated={(product) => {
            setProducts((current) => [product, ...current]);
            setShowAddProduct(false);
            pushToast("เพิ่มสินค้าแล้ว", product.title, "success");
          }}
          onError={(message) => pushToast("เพิ่มสินค้าไม่สำเร็จ", message, "danger")}
        />
      )}

      {editProduct && (
        <EditProductModal
          product={editProduct}
          onClose={() => setEditProduct(null)}
          onSaved={(product) => {
            setProducts((current) => current.map((item) => (item.id === product.id ? product : item)).sort((a, b) => Number(b.isPinned) - Number(a.isPinned)));
            setDetailProduct((current) => (current?.id === product.id ? product : current));
            setEditProduct(null);
            pushToast("แก้ไขสินค้าแล้ว", product.title, "success");
          }}
          onError={(message) => pushToast("แก้ไขสินค้าไม่สำเร็จ", message, "danger")}
        />
      )}

      {actionTarget && (
        <ProductActionModal
          target={actionTarget}
          now={now}
          onClose={() => setActionTarget(null)}
          onSaved={(product) => {
            setProducts((current) => current.map((item) => (item.id === product.id ? product : item)));
            setDetailProduct((current) => (current?.id === product.id ? product : current));
            setActionTarget(null);
            pushToast(actionTarget.action === "reserve" ? "จองสินค้าแล้ว" : "ส่งราคาประมูลแล้ว", product.title, "success");
          }}
          onError={(message) => pushToast("บันทึกไม่สำเร็จ", message, "danger")}
        />
      )}

      {detailProduct && (
        <ProductDetailModal
          product={detailProduct}
          isAdmin={isAdmin}
          busy={busyProductId === detailProduct.id}
          now={now}
          onClose={() => setDetailProduct(null)}
          onAction={(action) => {
            setDetailProduct(null);
            setActionTarget({ product: detailProduct, action });
          }}
          onDelete={() => void handleDelete(detailProduct)}
          onEdit={() => setEditProduct(detailProduct)}
          onExport={() => void handleExport(detailProduct)}
          onOpenImage={(imageIndex) => setLightbox({ product: detailProduct, imageIndex })}
          onModeChange={(mode) => void handleModeChange(detailProduct, mode)}
          onReset={() => void handleReset(detailProduct)}
          onShare={() => void handleShare(detailProduct)}
        />
      )}

      {lightbox && (
        <ImageLightbox
          product={lightbox.product}
          initialIndex={lightbox.imageIndex}
          onClose={() => setLightbox(null)}
        />
      )}

      {adminOpen && (
        <AdminModal
          isAdmin={isAdmin}
          username={adminUsername}
          onClose={() => setAdminOpen(false)}
          onLoggedIn={(username) => {
            setIsAdmin(true);
            setAdminUsername(username);
            pushToast("เข้าสู่ระบบ Admin แล้ว", username ? `บัญชี ${username}` : "เปิดสิทธิ์จัดการสินค้า", "success");
          }}
          onLoggedOut={() => {
            setIsAdmin(false);
            setAdminUsername(null);
            setAdminOpen(false);
            pushToast("ออกจาก Admin แล้ว", "ปิดสิทธิ์จัดการสินค้า", "neutral");
          }}
          onOpenEvent={(productId) => void openProductFromEvent(productId)}
          onError={(message) => pushToast("Admin error", message, "danger")}
        />
      )}
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ProductCard({
  product,
  isAdmin,
  busy,
  now,
  onAction,
  onDelete,
  onEdit,
  onExport,
  onOpenImage,
  onModeChange,
  onOpenDetails,
  onReset,
  onShare,
}: {
  product: Product;
  isAdmin: boolean;
  busy: boolean;
  now: number;
  onAction: (action: "reserve" | "bid") => void;
  onDelete: () => void;
  onEdit: () => void;
  onExport: () => void;
  onOpenImage: (imageIndex: number) => void;
  onModeChange: (mode: ProductMode) => void;
  onOpenDetails: () => void;
  onReset: () => void;
  onShare: () => void;
}) {
  const [activeImage, setActiveImage] = useState(0);
  const selectedImage = product.images[activeImage] ?? product.images[0];
  const canReserve = product.mode === "sale" && product.status === "available";
  const canReset = product.status === "reserved" || product.mode === "auction" || product.bidCount > 0;
  const auctionState = getAuctionState(product, now);
  const statusLabel =
    product.mode === "auction" ? "ประมูล" : product.status === "reserved" ? "จองแล้ว" : "พร้อมจอง";

  function handleCardClick(event: ReactMouseEvent<HTMLElement>) {
    if (!window.matchMedia("(max-width: 639px)").matches) return;
    if ((event.target as HTMLElement).closest("button")) return;
    onOpenDetails();
  }

  function handleCardKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (!window.matchMedia("(max-width: 639px)").matches) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onOpenDetails();
  }

  return (
    <article
      className="product-card"
      data-testid="product-card"
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      tabIndex={0}
      aria-label={`ดูรายละเอียด ${product.title}`}
    >
      <div className="product-media">
        {selectedImage ? (
          <button className="media-button" type="button" onClick={() => onOpenImage(activeImage)} aria-label={`ดูรูป ${product.title} แบบเต็มจอ`}>
            <img src={selectedImage.url} alt={product.title} loading="lazy" />
            <span className="image-zoom-pill">
              <Maximize2 size={14} aria-hidden="true" />
            </span>
          </button>
        ) : (
          <div className="image-placeholder">
            <Package size={36} aria-hidden="true" />
            <span>ไม่มีรูปสินค้า</span>
          </div>
        )}
        <span className={`status-pill ${product.mode === "auction" ? "auction" : product.status}`}>{statusLabel}</span>
        {product.isPinned && (
          <span className="featured-pill">
            <Star size={14} aria-hidden="true" />
            สินค้าแนะนำ
          </span>
        )}
        {product.mode === "auction" && auctionState.label && (
          <span className={`countdown-pill ${auctionState.isEnded ? "ended" : ""}`}>
            <Clock3 size={14} aria-hidden="true" />
            {auctionState.label}
          </span>
        )}
      </div>

      {product.images.length > 1 && (
        <div className="thumbnail-row" aria-label={`รูปสินค้า ${product.title}`}>
          {product.images.map((image, index) => (
            <button
              key={image.id}
              className={activeImage === index ? "active" : ""}
              type="button"
              onClick={() => setActiveImage(index)}
              aria-label={`ดูรูปที่ ${index + 1}`}
            >
              <img src={image.url} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}

      <div className="product-body">
        <div className="product-title-row">
          <div>
            <h2>{product.title}</h2>
            <p className="price">{formatBaht(product.priceCents)}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            onClick={onExport}
            disabled={busy}
            aria-label={`ส่งออก PDF ${product.title}`}
            data-testid="export-pdf"
          >
            <FileDown size={18} aria-hidden="true" />
          </button>
          <button className="icon-button" type="button" onClick={onShare} disabled={busy} aria-label={`แชร์ ${product.title}`} data-testid="share-product">
            <Share2 size={18} aria-hidden="true" />
          </button>
        </div>

        <p className="description">{product.description || "ไม่มีรายละเอียดเพิ่มเติม"}</p>

        <dl className="detail-list">
          <div>
            <dt>ขนาด</dt>
            <dd>{product.dimensions || "-"}</dd>
          </div>
          <div>
            <dt>Note</dt>
            <dd>{product.note || "-"}</dd>
          </div>
          <div>
            <dt>เพิ่มเมื่อ</dt>
            <dd>{formatDateTime(product.createdAt)}</dd>
          </div>
        </dl>

        {product.mode === "auction" && (
          <div className="auction-summary">
            <Gavel size={18} aria-hidden="true" />
            <span>
              {product.topBidCents ? `ราคาสูงสุด ${formatBaht(product.topBidCents)}` : "ยังไม่มีผู้ประมูล"}
              {product.latestBid?.bidderName ? ` โดย ${product.latestBid.bidderName}` : ""}
              {product.bidCount > 0 ? ` (${product.bidCount} ครั้ง)` : ""}
            </span>
          </div>
        )}

        {product.reservation && (
          <div className="reservation-summary">
            <CheckCircle2 size={18} aria-hidden="true" />
            <span>
              จองโดย {product.reservation.employeeName} / {product.reservation.phone}
            </span>
          </div>
        )}

        <p className="user-agent-line">UA: {compactUserAgent(product.userAgent)}</p>

        <div className="product-actions">
          {product.mode === "auction" ? (
            <button className="primary-button" type="button" onClick={() => onAction("bid")} disabled={busy || !auctionState.isActive} data-testid="bid-product">
              <Gavel size={18} aria-hidden="true" />
              {auctionState.isActive ? "เสนอราคา" : auctionState.label}
            </button>
          ) : (
            <button
              className="primary-button"
              type="button"
              onClick={() => onAction("reserve")}
              disabled={busy || !canReserve}
              data-testid="reserve-product"
            >
              <ShoppingBag size={18} aria-hidden="true" />
              {product.status === "reserved" ? "จองแล้ว" : "จองสินค้า"}
            </button>
          )}
        </div>

        {isAdmin && (
          <div className="admin-controls" aria-label={`Admin controls สำหรับ ${product.title}`}>
            <button className="text-button" type="button" onClick={onEdit} disabled={busy} data-testid="edit-product">
              <Edit3 size={16} aria-hidden="true" />
              แก้ไข
            </button>
            <button className="text-button" type="button" onClick={onReset} disabled={busy || !canReset} data-testid="reset-product">
              <RotateCcw size={16} aria-hidden="true" />
              รีเซ็ต
            </button>
            <button
              className="text-button"
              type="button"
              onClick={() => onModeChange(product.mode === "auction" ? "sale" : "auction")}
              disabled={busy}
              data-testid="toggle-mode"
            >
              {product.mode === "auction" ? "เปลี่ยนเป็นจอง" : "เปลี่ยนเป็นประมูล"}
            </button>
            <button className="danger-button" type="button" onClick={onDelete} disabled={busy} data-testid="delete-product">
              <Trash2 size={16} aria-hidden="true" />
              ลบ
            </button>
          </div>
        )}
      </div>
    </article>
  );
}

function ProductDetailModal({
  product,
  isAdmin,
  busy,
  now,
  onClose,
  onAction,
  onDelete,
  onEdit,
  onExport,
  onOpenImage,
  onModeChange,
  onReset,
  onShare,
}: {
  product: Product;
  isAdmin: boolean;
  busy: boolean;
  now: number;
  onClose: () => void;
  onAction: (action: "reserve" | "bid") => void;
  onDelete: () => void;
  onEdit: () => void;
  onExport: () => void;
  onOpenImage: (imageIndex: number) => void;
  onModeChange: (mode: ProductMode) => void;
  onReset: () => void;
  onShare: () => void;
}) {
  const [activeImage, setActiveImage] = useState(0);
  const [bids, setBids] = useState<BidHistory[]>([]);
  const [bidsLoading, setBidsLoading] = useState(false);
  const selectedImage = product.images[activeImage] ?? product.images[0];
  const canReserve = product.mode === "sale" && product.status === "available";
  const canReset = product.status === "reserved" || product.mode === "auction" || product.bidCount > 0;
  const auctionState = getAuctionState(product, now);
  const statusLabel =
    product.mode === "auction" ? "ประมูล" : product.status === "reserved" ? "จองแล้ว" : "พร้อมจอง";

  useEffect(() => {
    let active = true;
    if (!isAdmin || product.mode !== "auction") {
      setBids([]);
      return () => {
        active = false;
      };
    }
    setBidsLoading(true);
    void getProductBids(product.id)
      .then((data) => {
        if (active) setBids(data.bids);
      })
      .catch(() => {
        if (active) setBids([]);
      })
      .finally(() => {
        if (active) setBidsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isAdmin, product.id, product.mode, product.bidCount]);

  return (
    <Modal title="รายละเอียดสินค้า" onClose={onClose} wide>
      <article className="detail-sheet">
        <div className="detail-sheet-media">
          {selectedImage ? (
            <button className="media-button" type="button" onClick={() => onOpenImage(activeImage)} aria-label={`ดูรูป ${product.title} แบบเต็มจอ`}>
              <img src={selectedImage.url} alt={product.title} />
              <span className="image-zoom-pill">
                <Maximize2 size={14} aria-hidden="true" />
              </span>
            </button>
          ) : (
            <div className="image-placeholder">
              <Package size={36} aria-hidden="true" />
              <span>ไม่มีรูปสินค้า</span>
            </div>
          )}
          <span className={`status-pill ${product.mode === "auction" ? "auction" : product.status}`}>{statusLabel}</span>
          {product.isPinned && (
            <span className="featured-pill">
              <Star size={14} aria-hidden="true" />
              สินค้าแนะนำ
            </span>
          )}
          {product.mode === "auction" && auctionState.label && (
            <span className={`countdown-pill ${auctionState.isEnded ? "ended" : ""}`}>
              <Clock3 size={14} aria-hidden="true" />
              {auctionState.label}
            </span>
          )}
        </div>

        {product.images.length > 1 && (
          <div className="thumbnail-row visible" aria-label={`รูปสินค้า ${product.title}`}>
            {product.images.map((image, index) => (
              <button
                key={image.id}
                className={activeImage === index ? "active" : ""}
                type="button"
                onClick={() => setActiveImage(index)}
                aria-label={`ดูรูปที่ ${index + 1}`}
              >
                <img src={image.url} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}

        <div className="detail-sheet-body">
          <div className="product-title-row">
            <div>
              <h2>{product.title}</h2>
              <p className="price">{formatBaht(product.priceCents)}</p>
            </div>
            <div className="title-actions">
              <button className="icon-button" type="button" onClick={onShare} disabled={busy} aria-label={`แชร์ ${product.title}`}>
                <Share2 size={18} aria-hidden="true" />
              </button>
              <button className="icon-button" type="button" onClick={onExport} disabled={busy} aria-label={`ส่งออก PDF ${product.title}`}>
                <FileDown size={18} aria-hidden="true" />
              </button>
            </div>
          </div>

          <p className="description">{product.description || "ไม่มีรายละเอียดเพิ่มเติม"}</p>
          <dl className="detail-list">
            <div>
              <dt>ขนาด</dt>
              <dd>{product.dimensions || "-"}</dd>
            </div>
            <div>
              <dt>Note</dt>
              <dd>{product.note || "-"}</dd>
            </div>
            <div>
              <dt>เพิ่มเมื่อ</dt>
              <dd>{formatDateTime(product.createdAt)}</dd>
            </div>
          </dl>

          {product.mode === "auction" && (
            <div className="auction-summary">
              <Gavel size={18} aria-hidden="true" />
              <span>
                {product.topBidCents ? `ราคาสูงสุด ${formatBaht(product.topBidCents)}` : "ยังไม่มีผู้ประมูล"}
                {product.latestBid?.bidderName ? ` โดย ${product.latestBid.bidderName}` : ""}
                {product.bidCount > 0 ? ` (${product.bidCount} ครั้ง)` : ""}
              </span>
            </div>
          )}

          {product.reservation && (
            <div className="reservation-summary">
              <CheckCircle2 size={18} aria-hidden="true" />
              <span>
                จองโดย {product.reservation.employeeName} / {product.reservation.phone}
              </span>
            </div>
          )}

          <p className="user-agent-line">UA: {compactUserAgent(product.userAgent)}</p>

          <div className="product-actions">
            {product.mode === "auction" ? (
              <button className="primary-button" type="button" onClick={() => onAction("bid")} disabled={busy || !auctionState.isActive}>
                <Gavel size={18} aria-hidden="true" />
                {auctionState.isActive ? "เสนอราคา" : auctionState.label}
              </button>
            ) : (
              <button className="primary-button" type="button" onClick={() => onAction("reserve")} disabled={busy || !canReserve}>
                <ShoppingBag size={18} aria-hidden="true" />
                {product.status === "reserved" ? "จองแล้ว" : "จองสินค้า"}
              </button>
            )}
          </div>

          {isAdmin && (
            <div className="admin-controls" aria-label={`Admin controls สำหรับ ${product.title}`}>
              <button className="text-button" type="button" onClick={onEdit} disabled={busy}>
                <Edit3 size={16} aria-hidden="true" />
                แก้ไข
              </button>
              <button className="text-button" type="button" onClick={onReset} disabled={busy || !canReset}>
                <RotateCcw size={16} aria-hidden="true" />
                รีเซ็ต
              </button>
              <button
                className="text-button"
                type="button"
                onClick={() => onModeChange(product.mode === "auction" ? "sale" : "auction")}
                disabled={busy}
              >
                {product.mode === "auction" ? "เปลี่ยนเป็นจอง" : "เปลี่ยนเป็นประมูล"}
              </button>
              <button className="danger-button" type="button" onClick={onDelete} disabled={busy}>
                <Trash2 size={16} aria-hidden="true" />
                ลบ
              </button>
            </div>
          )}

          {isAdmin && product.mode === "auction" && (
            <section className="bid-history" aria-label="ประวัติการประมูล">
              <div className="section-title-row">
                <h3>ประวัติผู้เสนอราคา</h3>
                <span>{bidsLoading ? "กำลังโหลด" : `${bids.length} รายการ`}</span>
              </div>
              {bids.length === 0 ? (
                <p className="muted-text">ยังไม่มีประวัติการเสนอราคา</p>
              ) : (
                <div className="history-list">
                  {bids.map((bid) => (
                    <div className="history-item" key={bid.id}>
                      <div>
                        <strong>{bid.bidderName}</strong>
                        <p>{bid.phone}</p>
                      </div>
                      <div>
                        <strong>{formatBaht(bid.amountCents)}</strong>
                        <p>{formatDateTime(bid.createdAt)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </article>
    </Modal>
  );
}

function AddProductModal({
  onClose,
  onCreated,
  onError,
}: {
  onClose: () => void;
  onCreated: (product: Product) => void;
  onError: (message: string) => void;
}) {
  const [images, setImages] = useState<ConvertedImage[]>([]);
  const [converting, setConverting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imageError, setImageError] = useState("");
  const userAgent = navigator.userAgent;

  useEffect(() => {
    return () => {
      images.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    };
  }, [images]);

  async function handleImageChange(files: FileList | null) {
    if (!files) return;
    setConverting(true);
    setImageError("");
    try {
      const next = await convertToWebp(files);
      setImages((current) => {
        current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
        return next;
      });
    } catch (error) {
      setImageError(error instanceof Error ? error.message : "แปลงรูปไม่สำเร็จ");
    } finally {
      setConverting(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.delete("images");
    images.forEach((image) => formData.append("images", image.file));
    setSubmitting(true);
    try {
      const { product } = await createProduct(formData);
      onCreated(product);
    } catch (error) {
      onError(error instanceof Error ? error.message : "กรุณาลองใหม่");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="เพิ่มสินค้าใหม่" onClose={onClose} wide>
      <form className="stack-form" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="title">ชื่อสินค้า *</label>
          <input id="title" name="title" required maxLength={120} placeholder="เช่น โต๊ะทำงานปรับระดับ" />
        </div>

        <div className="field-grid">
          <div className="field">
            <label htmlFor="price">ราคา (บาท) *</label>
            <input id="price" name="price" required min="0" step="1" inputMode="decimal" type="number" placeholder="1200" />
          </div>
          <div className="field">
            <label htmlFor="dimensions">ขนาดสินค้า</label>
            <input id="dimensions" name="dimensions" maxLength={120} placeholder="กว้าง x ยาว x สูง" />
          </div>
        </div>

        <div className="field">
          <label htmlFor="description">รายละเอียดสินค้า</label>
          <textarea id="description" name="description" rows={4} maxLength={900} placeholder="สภาพสินค้า อุปกรณ์ที่มี จุดที่ควรรู้" />
        </div>

        <div className="field">
          <label htmlFor="note">Note</label>
          <textarea id="note" name="note" rows={3} maxLength={500} placeholder="เงื่อนไขการรับสินค้า จุดนัดรับ หรือหมายเหตุอื่น" />
        </div>

        <div className="upload-zone">
          <input
            id="images"
            name="images"
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => void handleImageChange(event.target.files)}
            data-testid="product-images"
          />
          <label htmlFor="images">
            <UploadCloud size={24} aria-hidden="true" />
            <span>อัปโหลดรูปได้สูงสุด 5 รูป</span>
            <small>{converting ? "กำลังแปลงเป็น WebP" : "รองรับการเลือกหลายรูปจากมือถือ"}</small>
          </label>
        </div>
        {imageError && <p className="field-error">{imageError}</p>}

        {images.length > 0 && (
          <div className="preview-grid" aria-label="รูปตัวอย่าง">
            {images.map((image) => (
              <figure key={image.previewUrl}>
                <img src={image.previewUrl} alt={image.originalName} />
                <figcaption>{image.file.name}</figcaption>
              </figure>
            ))}
          </div>
        )}

        <p className="user-agent-preview">User agent ที่จะแสดงในรายการ: {compactUserAgent(userAgent)}</p>

        <div className="form-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="primary-button" type="submit" disabled={submitting || converting} data-testid="submit-product">
            <ImagePlus size={18} aria-hidden="true" />
            {submitting ? "กำลังเพิ่ม" : "เพิ่มสินค้า"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditProductModal({
  product,
  onClose,
  onSaved,
  onError,
}: {
  product: Product;
  onClose: () => void;
  onSaved: (product: Product) => void;
  onError: (message: string) => void;
}) {
  const [images, setImages] = useState<ConvertedImage[]>([]);
  const [converting, setConverting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imageError, setImageError] = useState("");
  const [mode, setMode] = useState<ProductMode>(product.mode);
  const [coverImageId, setCoverImageId] = useState(product.coverImageId ?? product.images[0]?.id ?? "");
  const [coverUploadIndex, setCoverUploadIndex] = useState(0);

  useEffect(() => {
    return () => {
      images.forEach((image) => URL.revokeObjectURL(image.previewUrl));
    };
  }, [images]);

  async function handleImageChange(files: FileList | null) {
    if (!files) return;
    setConverting(true);
    setImageError("");
    try {
      const next = await convertToWebp(files);
      setImages((current) => {
        current.forEach((image) => URL.revokeObjectURL(image.previewUrl));
        return next;
      });
      setCoverUploadIndex(0);
    } catch (error) {
      setImageError(error instanceof Error ? error.message : "แปลงรูปไม่สำเร็จ");
    } finally {
      setConverting(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    formData.delete("images");
    formData.set("mode", mode);
    formData.set("isPinned", formData.get("isPinned") ? "1" : "0");
    formData.set("auctionStartAt", mode === "auction" ? localDatetimeToIso(formData.get("auctionStartAt")) : "");
    formData.set("auctionEndAt", mode === "auction" ? localDatetimeToIso(formData.get("auctionEndAt")) : "");
    if (images.length > 0) {
      formData.delete("coverImageId");
      formData.set("coverUploadIndex", String(coverUploadIndex));
      images.forEach((image) => formData.append("images", image.file));
    } else {
      formData.set("coverImageId", coverImageId);
    }

    setSubmitting(true);
    try {
      const { product: updated } = await updateProduct(product.id, formData);
      onSaved(updated);
    } catch (error) {
      onError(error instanceof Error ? error.message : "กรุณาลองใหม่");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="แก้ไขสินค้า" onClose={onClose} wide>
      <form className="stack-form" onSubmit={handleSubmit}>
        <div className="field">
          <label htmlFor="edit-title">ชื่อสินค้า *</label>
          <input id="edit-title" name="title" required maxLength={120} defaultValue={product.title} />
        </div>

        <div className="field-grid">
          <div className="field">
            <label htmlFor="edit-price">ราคา (บาท) *</label>
            <input id="edit-price" name="price" required min="0" step="1" inputMode="decimal" type="number" defaultValue={product.priceCents / 100} />
          </div>
          <div className="field">
            <label htmlFor="edit-dimensions">ขนาดสินค้า</label>
            <input id="edit-dimensions" name="dimensions" maxLength={120} defaultValue={product.dimensions} />
          </div>
        </div>

        <div className="field-grid">
          <div className="field">
            <label htmlFor="edit-mode">รูปแบบรายการ</label>
            <select id="edit-mode" name="mode" value={mode} onChange={(event) => setMode(event.target.value as ProductMode)}>
              <option value="sale">จองสินค้า</option>
              <option value="auction">ประมูลสินค้า</option>
            </select>
          </div>
          <label className="checkbox-row">
            <input name="isPinned" type="checkbox" defaultChecked={product.isPinned} />
            <span>
              <Pin size={16} aria-hidden="true" />
              ปักหมุดเป็นสินค้าแนะนำ
            </span>
          </label>
        </div>

        {mode === "auction" && (
          <div className="field-grid">
            <div className="field">
              <label htmlFor="auction-start">เริ่มประมูล</label>
              <input id="auction-start" name="auctionStartAt" type="datetime-local" defaultValue={toDatetimeLocal(product.auctionStartAt)} />
            </div>
            <div className="field">
              <label htmlFor="auction-end">สิ้นสุดประมูล</label>
              <input id="auction-end" name="auctionEndAt" type="datetime-local" defaultValue={toDatetimeLocal(product.auctionEndAt)} />
            </div>
          </div>
        )}

        <div className="field">
          <label htmlFor="edit-description">รายละเอียดสินค้า</label>
          <textarea id="edit-description" name="description" rows={4} maxLength={900} defaultValue={product.description} />
        </div>

        <div className="field">
          <label htmlFor="edit-note">Note</label>
          <textarea id="edit-note" name="note" rows={3} maxLength={500} defaultValue={product.note} />
        </div>

        {product.images.length > 0 && images.length === 0 && (
          <section className="cover-picker" aria-label="เลือกรูปหน้าปกจากรูปเดิม">
            <div className="section-title-row">
              <h3>รูปหน้าปก</h3>
              <span>เลือกรูปเดิม</span>
            </div>
            <div className="cover-grid">
              {product.images.map((image) => (
                <label key={image.id} className={coverImageId === image.id ? "active" : ""}>
                  <input
                    name="coverImageId"
                    type="radio"
                    value={image.id}
                    checked={coverImageId === image.id}
                    onChange={() => setCoverImageId(image.id)}
                  />
                  <img src={image.url} alt="" loading="lazy" />
                </label>
              ))}
            </div>
          </section>
        )}

        <div className="upload-zone">
          <input
            id="edit-images"
            name="images"
            type="file"
            accept="image/*"
            multiple
            onChange={(event) => void handleImageChange(event.target.files)}
          />
          <label htmlFor="edit-images">
            <UploadCloud size={24} aria-hidden="true" />
            <span>อัปโหลดรูปใหม่ได้สูงสุด 5 รูป</span>
            <small>{converting ? "กำลังแปลงเป็น WebP" : "ถ้าเลือกชุดใหม่ ระบบจะแทนที่รูปเดิมทั้งหมด"}</small>
          </label>
        </div>
        {imageError && <p className="field-error">{imageError}</p>}

        {images.length > 0 && (
          <section className="cover-picker" aria-label="เลือกรูปหน้าปกจากรูปใหม่">
            <div className="section-title-row">
              <h3>รูปหน้าปกใหม่</h3>
              <span>ชุดรูปใหม่</span>
            </div>
            <div className="cover-grid">
              {images.map((image, index) => (
                <label key={image.previewUrl} className={coverUploadIndex === index ? "active" : ""}>
                  <input
                    name="coverUploadIndex"
                    type="radio"
                    value={index}
                    checked={coverUploadIndex === index}
                    onChange={() => setCoverUploadIndex(index)}
                  />
                  <img src={image.previewUrl} alt={image.originalName} />
                </label>
              ))}
            </div>
          </section>
        )}

        <div className="form-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="primary-button" type="submit" disabled={submitting || converting}>
            <Edit3 size={18} aria-hidden="true" />
            {submitting ? "กำลังบันทึก" : "บันทึกสินค้า"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ProductActionModal({
  target,
  now,
  onClose,
  onSaved,
  onError,
}: {
  target: ActionTarget;
  now: number;
  onClose: () => void;
  onSaved: (product: Product) => void;
  onError: (message: string) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const isBid = target.action === "bid";
  const auctionState = getAuctionState(target.product, now);
  const minimumBid = Math.max(target.product.priceCents, target.product.topBidCents ?? 0) / 100 + 1;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    setSubmitting(true);
    try {
      if (isBid) {
        const { product } = await bidProduct(target.product.id, {
          bidderName: String(formData.get("name") ?? ""),
          phone: String(formData.get("phone") ?? ""),
          amount: Number(formData.get("amount") ?? 0),
        });
        onSaved(product);
      } else {
        const { product } = await reserveProduct(target.product.id, {
          employeeName: String(formData.get("name") ?? ""),
          phone: String(formData.get("phone") ?? ""),
        });
        onSaved(product);
      }
    } catch (error) {
      onError(error instanceof Error ? error.message : "กรุณาลองใหม่");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={isBid ? "เสนอราคาประมูล" : "จองสินค้า"} onClose={onClose}>
      <form className="stack-form" onSubmit={handleSubmit}>
        <div className="compact-product">
          <strong>{target.product.title}</strong>
          <span>{isBid ? `ราคาเริ่มต้น ${formatBaht(target.product.priceCents)}` : formatBaht(target.product.priceCents)}</span>
          {isBid && auctionState.label && <small>{auctionState.label}</small>}
        </div>

        <div className="field">
          <label htmlFor="employee-name">{isBid ? "ชื่อผู้ประมูล *" : "ชื่อผู้จอง *"}</label>
          <div className="input-with-icon">
            <UserRound size={18} aria-hidden="true" />
            <input id="employee-name" name="name" required autoComplete="name" maxLength={80} />
          </div>
        </div>

        <div className="field">
          <label htmlFor="employee-phone">เบอร์โทร *</label>
          <div className="input-with-icon">
            <Phone size={18} aria-hidden="true" />
            <input id="employee-phone" name="phone" required autoComplete="tel" inputMode="tel" maxLength={30} />
          </div>
        </div>

        {isBid && (
          <div className="field">
            <label htmlFor="bid-amount">ราคาเสนอ (บาท) *</label>
            <input id="bid-amount" name="amount" required min={minimumBid} step="1" inputMode="decimal" type="number" />
            <p className="field-hint">ต้องมากกว่า {formatBaht(Math.max(target.product.priceCents, target.product.topBidCents ?? 0))}</p>
          </div>
        )}

        <div className="form-actions">
          <button className="secondary-button" type="button" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="primary-button"
            type="submit"
            disabled={submitting || (isBid && !auctionState.isActive)}
            data-testid={isBid ? "submit-bid" : "submit-reservation"}
          >
            {isBid ? <Gavel size={18} aria-hidden="true" /> : <ShoppingBag size={18} aria-hidden="true" />}
            {submitting ? "กำลังบันทึก" : isBid && !auctionState.isActive ? auctionState.label : isBid ? "ส่งราคา" : "ยืนยันจอง"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function AdminModal({
  isAdmin,
  username,
  onClose,
  onLoggedIn,
  onLoggedOut,
  onOpenEvent,
  onError,
}: {
  isAdmin: boolean;
  username: string | null;
  onClose: () => void;
  onLoggedIn: (username: string | null) => void;
  onLoggedOut: () => void;
  onOpenEvent: (productId: string | null) => void;
  onError: (message: string) => void;
}) {
  const [loginUsername, setLoginUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [events, setEvents] = useState<LiveEvent[]>([]);
  const [loadingAdminData, setLoadingAdminData] = useState(false);
  const [newAdminUsername, setNewAdminUsername] = useState("admin2");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [passwordDrafts, setPasswordDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isAdmin) return;
    void refreshAdminData();
  }, [isAdmin]);

  async function refreshAdminData() {
    setLoadingAdminData(true);
    try {
      const [adminData, eventData] = await Promise.all([getAdmins(), getAdminEvents()]);
      setAdmins(adminData.admins);
      setEvents(eventData.events);
    } catch (error) {
      onError(error instanceof Error ? error.message : "โหลดข้อมูล Admin ไม่สำเร็จ");
    } finally {
      setLoadingAdminData(false);
    }
  }

  async function handleLogin(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      const status = await loginAdmin(loginUsername, password);
      onLoggedIn(status.username ?? loginUsername);
      setPassword("");
    } catch (error) {
      onError(error instanceof Error ? error.message : "กรุณาลองใหม่");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogout() {
    setSubmitting(true);
    try {
      await logoutAdmin();
      onLoggedOut();
    } catch (error) {
      onError(error instanceof Error ? error.message : "กรุณาลองใหม่");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCreateAdmin(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      await createAdmin({ username: newAdminUsername, password: newAdminPassword });
      setNewAdminUsername("admin2");
      setNewAdminPassword("");
      await refreshAdminData();
    } catch (error) {
      onError(error instanceof Error ? error.message : "เพิ่ม Admin ไม่สำเร็จ");
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePasswordChange(admin: AdminAccount) {
    const nextPassword = passwordDrafts[admin.id] ?? "";
    if (!nextPassword) {
      onError("กรุณาใส่รหัสผ่านใหม่");
      return;
    }
    setSubmitting(true);
    try {
      await updateAdminPassword(admin.id, nextPassword);
      setPasswordDrafts((current) => ({ ...current, [admin.id]: "" }));
      await refreshAdminData();
    } catch (error) {
      onError(error instanceof Error ? error.message : "เปลี่ยนรหัสผ่านไม่สำเร็จ");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Admin account" onClose={onClose} wide>
      {isAdmin ? (
        <div className="admin-panel">
          <div className="admin-state">
            <ShieldCheck size={24} aria-hidden="true" />
            <div>
              <strong>Admin active</strong>
              <p>{username ? `เข้าสู่ระบบด้วย ${username}` : "พร้อมจัดการสินค้า บัญชี และแจ้งเตือน"}</p>
            </div>
          </div>

          <section className="admin-section">
            <div className="section-title-row">
              <h3>
                <Users size={18} aria-hidden="true" />
                Admin accounts
              </h3>
              <button className="icon-button" type="button" onClick={() => void refreshAdminData()} disabled={loadingAdminData} aria-label="โหลดข้อมูล Admin ใหม่">
                <RefreshCw size={18} aria-hidden="true" />
              </button>
            </div>

            <form className="admin-inline-form" onSubmit={handleCreateAdmin}>
              <div className="field">
                <label htmlFor="new-admin-username">ชื่อ Admin ใหม่</label>
                <input
                  id="new-admin-username"
                  value={newAdminUsername}
                  onChange={(event) => setNewAdminUsername(event.target.value)}
                  autoComplete="username"
                />
              </div>
              <div className="field">
                <label htmlFor="new-admin-password">รหัสผ่านใหม่</label>
                <input
                  id="new-admin-password"
                  type="password"
                  value={newAdminPassword}
                  onChange={(event) => setNewAdminPassword(event.target.value)}
                  autoComplete="new-password"
                />
              </div>
              <button className="primary-button" type="submit" disabled={submitting}>
                <Users size={18} aria-hidden="true" />
                เพิ่ม Admin
              </button>
            </form>

            <div className="admin-account-list">
              {admins.map((admin) => (
                <div className="admin-account-row" key={admin.id}>
                  <div>
                    <strong>{admin.username}</strong>
                    <p>อัปเดต {formatDateTime(admin.updatedAt)}</p>
                  </div>
                  <div className="password-row">
                    <input
                      type="password"
                      placeholder="รหัสผ่านใหม่"
                      value={passwordDrafts[admin.id] ?? ""}
                      onChange={(event) => setPasswordDrafts((current) => ({ ...current, [admin.id]: event.target.value }))}
                      autoComplete="new-password"
                    />
                    <button className="secondary-button" type="button" onClick={() => void handlePasswordChange(admin)} disabled={submitting}>
                      <KeyRound size={16} aria-hidden="true" />
                      เปลี่ยน
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="admin-section">
            <div className="section-title-row">
              <h3>
                <History size={18} aria-hidden="true" />
                Notification history
              </h3>
              <span>{loadingAdminData ? "กำลังโหลด" : `${events.length} รายการ`}</span>
            </div>
            <div className="event-history">
              {events.length === 0 ? (
                <p className="muted-text">ยังไม่มีแจ้งเตือนย้อนหลัง</p>
              ) : (
                events.map((event) => (
                  <button key={event.id} type="button" onClick={() => onOpenEvent(event.productId)} disabled={!event.productId}>
                    <span>
                      <ListChecks size={16} aria-hidden="true" />
                      {event.message}
                    </span>
                    <small>{formatDateTime(event.createdAt)}</small>
                  </button>
                ))
              )}
            </div>
          </section>

          <button className="danger-button full-width" type="button" onClick={() => void handleLogout()} disabled={submitting}>
            <LogOut size={18} aria-hidden="true" />
            ออกจากระบบ Admin
          </button>
        </div>
      ) : (
        <form className="stack-form" onSubmit={handleLogin}>
          <div className="field">
            <label htmlFor="admin-username">Admin username *</label>
            <input
              id="admin-username"
              value={loginUsername}
              onChange={(event) => setLoginUsername(event.target.value)}
              required
              autoComplete="username"
            />
          </div>
          <div className="field">
            <label htmlFor="admin-password">Admin password *</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <div className="form-actions">
            <button className="secondary-button" type="button" onClick={onClose}>
              ยกเลิก
            </button>
            <button className="primary-button" type="submit" disabled={submitting} data-testid="admin-login-submit">
              <ShieldCheck size={18} aria-hidden="true" />
              {submitting ? "กำลังตรวจสอบ" : "เข้าสู่ระบบ"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}

function ImageLightbox({
  product,
  initialIndex,
  onClose,
}: {
  product: Product;
  initialIndex: number;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const images = product.images;
  const image = images[index] ?? images[0];
  const hasManyImages = images.length > 1;

  function showPrevious() {
    if (!images.length) return;
    setIndex((current) => (current - 1 + images.length) % images.length);
  }

  function showNext() {
    if (!images.length) return;
    setIndex((current) => (current + 1) % images.length);
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") showPrevious();
      if (event.key === "ArrowRight") showNext();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length, onClose]);

  if (!image) return null;

  return (
    <div className="lightbox-backdrop" role="dialog" aria-modal="true" aria-label={`รูปสินค้า ${product.title}`}>
      <div className="lightbox-header">
        <div>
          <strong>{product.title}</strong>
          <span>
            {index + 1}/{images.length}
          </span>
        </div>
        <button className="icon-button" type="button" onClick={onClose} aria-label="ปิดรูปภาพ">
          <X size={20} aria-hidden="true" />
        </button>
      </div>

      <div className="lightbox-stage">
        {hasManyImages && (
          <button className="lightbox-nav previous" type="button" onClick={showPrevious} aria-label="รูปก่อนหน้า">
            <ChevronLeft size={28} aria-hidden="true" />
          </button>
        )}
        <img src={image.url} alt={product.title} />
        {hasManyImages && (
          <button className="lightbox-nav next" type="button" onClick={showNext} aria-label="รูปถัดไป">
            <ChevronRight size={28} aria-hidden="true" />
          </button>
        )}
      </div>

      {hasManyImages && (
        <div className="lightbox-thumbnails" aria-label="เลือกรูปสินค้า">
          {images.map((item, itemIndex) => (
            <button
              key={item.id}
              className={itemIndex === index ? "active" : ""}
              type="button"
              onClick={() => setIndex(itemIndex)}
              aria-label={`ดูรูปที่ ${itemIndex + 1}`}
            >
              <img src={item.url} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onCloseRef.current();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      previous?.focus?.();
    };
  }, []);

  return (
    <div className="modal-backdrop" role="presentation">
      <div
        className={`modal-panel ${wide ? "wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <div className="modal-header">
          <h2 id="modal-title">{title}</h2>
          <button className="icon-button" type="button" onClick={onClose} aria-label="ปิด">
            <X size={20} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ToastStack({ toasts, onDismiss }: { toasts: ToastMessage[]; onDismiss: (id: number) => void }) {
  return (
    <div className="toast-stack" aria-live="polite" aria-atomic="false">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast ${toast.tone}`}>
          {toast.tone === "danger" ? <AlertTriangle size={20} aria-hidden="true" /> : <Bell size={20} aria-hidden="true" />}
          <div>
            <strong>{toast.title}</strong>
            <p>{toast.message}</p>
          </div>
          <button className="toast-close" type="button" onClick={() => onDismiss(toast.id)} aria-label="ปิดข้อความแจ้งเตือน">
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <section className="empty-state">
      <Package size={44} aria-hidden="true" />
      <h2>ยังไม่มีรายการที่ตรงกับตัวกรอง</h2>
      <p>เพิ่มสินค้าใหม่พร้อมรูป รายละเอียด ราคา และ note เพื่อให้พนักงานจองหรือประมูลได้</p>
      <button className="primary-button" type="button" onClick={onAdd}>
        <Plus size={20} aria-hidden="true" />
        เพิ่มสินค้า
      </button>
    </section>
  );
}

function ProductSkeleton() {
  return (
    <div className="product-grid" aria-label="กำลังโหลดสินค้า">
      {[0, 1, 2].map((item) => (
        <div className="product-card skeleton-card" key={item}>
          <div className="skeleton media" />
          <div className="skeleton line wide" />
          <div className="skeleton line" />
          <div className="skeleton line short" />
        </div>
      ))}
    </div>
  );
}
